import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Info, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { inputClass } from "@/components/Panel";
import { Badge, Card } from "@/components/ui";
import { documentTypes, numberingKeys, numberingQuery, previewNumber, updateSeries, type NumberingSeries } from "@/features/numbering/api";
import { useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { useCan } from "@/lib/session";
import { TableToolbar } from "@/components/TableToolbar";
import { useTableView, type TableColumn } from "@/components/TableView";
import type { ExportColumn } from "@/lib/exportXlsx";

// Settings → Numbering: one series per document type and company, edited in
// place (prefix with {YYYY} / {YY}, padding, active). A handful of rows per
// company, created on the first document of each type — so a plain table,
// no import (nothing to bulk-load), changes audited by the table trigger.

export const Route = createFileRoute("/_app/settings/numbering")({
  component: NumberingPage,
});

function NumberingPage() {
  const { t } = useTranslation();
  const { company } = useActiveCompany();
  const canRead = useCan("core.numbering.read");
  const canManage = useCan("core.numbering.manage");
  const series = useQuery({ ...numberingQuery(company?.id ?? ""), enabled: !!company && canRead });
  const allRows = useMemo(() => series.data ?? [], [series.data]);
  // One row per document type: column filters and sort in the browser.
  const seriesColumns = useMemo<TableColumn<NumberingSeries>[]>(
    () => [
      { key: "document", header: t("numbering.colDocument"), type: "text", value: (s) => t(`numbering.type.${s.document_type}`, { defaultValue: s.document_type }) },
      { key: "prefix", header: t("numbering.colPrefix"), type: "text", value: (s) => s.prefix },
      { key: "padding", header: t("numbering.colPadding"), type: "number", value: (s) => s.padding },
      { key: "next", header: t("numbering.colNext"), type: "number", value: (s) => s.next_number, align: "end" },
      { key: "active", header: t("numbering.colActive"), type: "bool", value: (s) => s.is_active },
    ],
    [t],
  );
  const table = useTableView(allRows, seriesColumns);
  const rows = table.rows;
  const exportColumns: ExportColumn<NumberingSeries>[] = [
    { header: t("numbering.colDocument"), kind: "text", value: (r) => t(`numbering.type.${r.document_type}`, { defaultValue: r.document_type }) },
    { header: "code", kind: "code", value: (r) => r.code },
    { header: t("numbering.colPrefix"), kind: "code", value: (r) => r.prefix },
    { header: t("numbering.colPadding"), kind: "integer", value: (r) => r.padding, sum: false },
    { header: t("numbering.colNext"), kind: "integer", value: (r) => r.next_number, sum: false },
    { header: t("numbering.colPreview"), kind: "code", value: (r) => previewNumber(r) },
    { header: t("numbering.colActive"), kind: "boolean", value: (r) => r.is_active },
  ];
  // Types without a series yet are shown only on the unfiltered list.
  const missing = table.filtered ? [] : Object.keys(documentTypes).filter((d) => !allRows.some((s) => s.document_type === d));

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-0.5">
        <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
          {t("nav.settingsGroup.title")} <span aria-hidden="true">/</span> {t("nav.settingsGroup.numbering")}
        </nav>
        <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("numbering.title")}</h1>
      </header>
      {!canRead ? (
        <p className="text-[13px] text-ink-muted">{t("numbering.noPermission")}</p>
      ) : (
        <>
          <p className="flex items-center gap-2 self-start rounded-lg bg-surface-muted px-3 py-1.5 text-[13px] text-ink-muted">
            <Info size={14} aria-hidden="true" />
            {t("numbering.note")}
          </p>
          <Card aria-label={t("numbering.title")} className="overflow-hidden">
            <TableToolbar
              label={t("numbering.title")}
              tools={{ id: "numbering", title: t("numbering.title") }}
              exportColumns={exportColumns}
              rows={rows}
              onRefresh={() => void series.refetch()}
              refreshing={series.isRefetching}
              hasView={table.hasView}
              onClearView={table.clear}
            />
            {table.chips && <div className="border-b border-border-subtle px-3 py-2">{table.chips}</div>}
            {table.popover}
            <div className="overflow-x-auto">
              <table className="tabular w-full min-w-[760px] border-collapse text-[13px]" aria-label={t("numbering.title")}>
                <thead>
                  <tr className="bg-surface-muted text-left text-ink-muted">
                    {table.th("document")}
                    {table.th("prefix")}
                    {table.th("padding")}
                    {table.th("next", "text-right")}
                    <th scope="col" className="px-4 py-2.5 font-medium">{t("numbering.colPreview")}</th>
                    {table.th("active")}
                    <th scope="col" className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {series.isPending
                    ? [0, 1, 2, 3].map((i) => (
                        <tr key={i} aria-hidden="true" className="border-t border-border-subtle" style={{ height: "var(--row-h)" }}>
                          <td colSpan={7} className="px-4">
                            <span className="block h-4 w-2/3 animate-pulse rounded bg-surface-muted" />
                          </td>
                        </tr>
                      ))
                    : rows.map((s) => <SeriesRow key={s.id} series={s} canManage={canManage} />)}
                  {!series.isPending &&
                    missing.map((d) => (
                      <tr key={d} className="border-t border-border-subtle text-ink-muted" style={{ height: "var(--row-h)" }}>
                        <td className="px-4">{t(`numbering.type.${d}`, { defaultValue: d })}</td>
                        <td className="px-4 font-mono text-xs">{documentTypes[d]}</td>
                        <td className="px-4">5</td>
                        <td className="px-4 text-right">1</td>
                        <td className="px-4 font-mono text-xs">{previewNumber({ prefix: documentTypes[d] ?? "", padding: 5, next_number: 1 })}</td>
                        <td className="px-4" colSpan={2}>
                          {t("numbering.onFirstUse")}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function SeriesRow({ series: s, canManage }: { series: NumberingSeries; canManage: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [prefix, setPrefix] = useState(s.prefix);
  const [padding, setPadding] = useState(String(s.padding));
  const [active, setActive] = useState(s.is_active);
  const [error, setError] = useState<string | null>(null);
  const pad = Number(padding);
  const valid = prefix.length <= 24 && Number.isInteger(pad) && pad >= 1 && pad <= 12;
  const dirty = prefix !== s.prefix || pad !== s.padding || active !== s.is_active;

  const save = useMutation({
    mutationFn: () => updateSeries(s.id, { prefix, padding: pad, is_active: active }),
    onSuccess: () => {
      setError(null);
      return queryClient.invalidateQueries({ queryKey: numberingKeys.all });
    },
    onError: () => setError(t("numbering.errSave")),
  });

  return (
    <tr className="border-t border-border-subtle" style={{ height: "var(--row-h)" }}>
      <td className="px-4">
        {t(`numbering.type.${s.document_type}`, { defaultValue: s.document_type })}
        {s.code !== "DEFAULT" && <span className="ml-1.5 font-mono text-xs text-ink-muted">{s.code}</span>}
      </td>
      <td className="px-4 py-1.5">
        <input
          aria-label={t("numbering.colPrefix")}
          value={prefix}
          maxLength={24}
          disabled={!canManage}
          onChange={(e) => setPrefix(e.target.value)}
          className={cn(inputClass, "h-8 w-40 font-mono text-xs")}
        />
      </td>
      <td className="px-4 py-1.5">
        <input
          aria-label={t("numbering.colPadding")}
          inputMode="numeric"
          value={padding}
          disabled={!canManage}
          onChange={(e) => setPadding(e.target.value)}
          className={cn(inputClass, "h-8 w-16 text-right", !valid && "border-(--status-danger-fg)")}
        />
      </td>
      <td className="px-4 text-right">{s.next_number}</td>
      <td className="px-4 font-mono text-xs">{valid ? previewNumber({ prefix, padding: pad, next_number: s.next_number }) : "—"}</td>
      <td className="px-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={active}
            disabled={!canManage}
            onChange={(e) => setActive(e.target.checked)}
            className="size-4 accent-(--accent)"
          />
          {active ? <Badge tone="success">{t("numbering.active")}</Badge> : <Badge tone="warning">{t("numbering.inactive")}</Badge>}
        </label>
      </td>
      <td className="px-4 text-right">
        {canManage && dirty && (
          <button
            type="button"
            disabled={!valid || save.isPending}
            onClick={() => save.mutate()}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-accent px-3 text-xs font-medium text-on-accent hover:bg-accent-hover disabled:opacity-60"
          >
            {save.isPending && <Loader2 size={13} className="animate-spin" aria-hidden="true" />}
            {t("form.save")}
          </button>
        )}
        {error && (
          <p role="alert" className="text-xs text-(--status-danger-fg)">
            {error}
          </p>
        )}
      </td>
    </tr>
  );
}
